import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useUser } from "@clerk/react";
import { getLeaderboard } from "@workspace/api-client-react";
import { Trophy, ArrowLeft, Medal } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import LanguagePill from "@/components/LanguagePill";

const TERRACOTTA = "#D9B8A3";
const CLAY = "#E8DAB2";
const SAGE = "#A8B5A2";

const TIER_META: Record<string, { color: string; label: string }> = {
  bronze:   { color: "#CD7F32", label: "Bronze"   },
  silver:   { color: "#A8A9AD", label: "Silver"   },
  gold:     { color: "#D4AF37", label: "Gold"     },
  platinum: { color: "#7B90C4", label: "Platinum" },
  none:     { color: "var(--muted-foreground)", label: "" },
};

export default function Leaderboard() {
  const [, navigate] = useLocation();
  const { isSignedIn } = useUser();

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["leaderboard"],
    queryFn: getLeaderboard,
    refetchInterval: 60000,
  });

  return (
    <div style={{ minHeight: "100vh", background: "var(--background)", color: "var(--foreground)", position: "relative", overflow: "hidden" }}>
      {/* Contemporary/landscape — ghost texture at 5% */}
      <img
        src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&q=80&fit=crop&auto=format"
        alt="" aria-hidden loading="lazy"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.13, filter: "grayscale(0.15)", pointerEvents: "none" }}
      />
      {/* Nav */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 40,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 24px", height: "56px",
        borderBottom: "1px solid var(--border)",
        background: "rgba(252,252,252,0.88)",
        backdropFilter: "blur(8px)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            onClick={() => navigate("/")}
            style={{
              display: "flex", alignItems: "center", gap: "6px",
              background: "none", border: "none", color: "var(--muted-foreground)",
              fontSize: "13px", cursor: "pointer", fontWeight: 300,
              padding: "4px 0",
            }}
          >
            <ArrowLeft size={14} />
            Home
          </button>
          <div style={{ width: "1px", height: "16px", background: "var(--border)" }} />
          <LogoMark size={20} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <LanguagePill />
          {!isSignedIn && (
            <button
              onClick={() => navigate("/sign-up")}
              style={{
                background: TERRACOTTA, border: "none", borderRadius: "8px",
                padding: "7px 18px", color: "#fff", fontSize: "13px", fontWeight: 400,
                cursor: "pointer",
              }}
            >
              Get Started
            </button>
          )}
        </div>
      </nav>

      {/* Header */}
      <div style={{ maxWidth: "720px", margin: "0 auto", padding: "60px 20px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "10px" }}>
          <Trophy size={28} color={TERRACOTTA} strokeWidth={1.5} />
          <h1 style={{ fontSize: "clamp(26px, 4vw, 40px)", fontWeight: 200, letterSpacing: "-0.03em", margin: 0 }}>
            Donor Leaderboard
          </h1>
        </div>
        <p style={{ color: "var(--muted-foreground)", fontWeight: 300, fontSize: "14px", lineHeight: 1.7, marginBottom: "0" }}>
          All-time rankings by total amount donated. Every contribution goes directly toward settling real debt.
        </p>

        {/* Tier legend */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "28px" }}>
          {(["bronze", "silver", "gold", "platinum"] as const).map((tier) => {
            const m = TIER_META[tier];
            const thresholds: Record<string, string> = {
              bronze: "$50", silver: "$250", gold: "$1,000", platinum: "$5,000",
            };
            return (
              <div key={tier} style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "5px 12px",
                border: `1px solid ${m.color}55`,
                borderRadius: "20px",
                background: `${m.color}10`,
                fontSize: "12px",
                color: m.color,
              }}>
                <span>{m.emoji}</span>
                <span style={{ fontWeight: 400 }}>{m.label}</span>
                <span style={{ color: "var(--muted-foreground)", fontSize: "11px" }}>≥ {thresholds[tier]}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Leaderboard list */}
      <div style={{ maxWidth: "720px", margin: "0 auto", padding: "0 20px 80px" }}>
        {isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {[...Array(8)].map((_, i) => (
              <div key={i} style={{
                height: "68px",
                background: "var(--muted)", borderRadius: "12px",
                opacity: 1 - i * 0.1,
                animation: "pulse 2s infinite",
              }} />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "80px 20px",
            color: "var(--muted-foreground)", fontWeight: 300, fontSize: "15px",
          }}>
            <Medal size={40} strokeWidth={1} style={{ marginBottom: "16px", opacity: 0.4 }} />
            <p>No donors yet. Be the first to make an impact.</p>
            <button
              onClick={() => navigate("/sign-up")}
              style={{
                marginTop: "20px",
                background: TERRACOTTA, border: "none", borderRadius: "8px",
                padding: "10px 24px", color: "#fff", fontSize: "13px", cursor: "pointer",
              }}
            >
              Donate now →
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {/* Top 3 podium-style cards */}
            {entries.slice(0, 3).map((entry, idx) => {
              const tier = entry.tier as string;
              const meta = TIER_META[tier] ?? TIER_META.none;
              const isCurrentUser = entry.isCurrentUser;
              return (
                <div
                  key={entry.rank}
                  style={{
                    display: "flex", alignItems: "center", gap: "16px",
                    padding: "18px 22px",
                    border: isCurrentUser
                      ? "1px solid var(--primary)"
                      : `1px solid ${idx === 0 ? `${CLAY}88` : "var(--border)"}`,
                    borderRadius: "14px",
                  }}
                >
                  <span style={{
                    fontSize: "12px", fontWeight: 600, minWidth: "32px", textAlign: "center",
                    color: idx === 0 ? "#D4AF37" : idx === 1 ? "#A8A9AD" : "#CD7F32",
                    letterSpacing: "0.04em", fontVariantNumeric: "tabular-nums",
                  }}>
                    {`0${idx + 1}`}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "16px", fontWeight: 300, color: "var(--foreground)" }}>
                        {entry.displayName}
                      </span>
                      {isCurrentUser && (
                        <span style={{
                          fontSize: "10px", padding: "1px 7px",
                          borderRadius: "10px", border: "1px solid var(--primary)",
                          color: "var(--primary)", fontWeight: 400,
                        }}>you</span>
                      )}
                      {tier !== "none" && (
                        <span style={{
                          fontSize: "11px", padding: "2px 9px",
                          borderRadius: "12px",
                          border: `1px solid ${meta.color}66`,
                          color: meta.color, background: `${meta.color}12`,
                        }}>
                          {meta.emoji} {meta.label}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--muted-foreground)", marginTop: "3px" }}>
                      {entry.debtsErased} debt{entry.debtsErased !== 1 ? "s" : ""} erased
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: "17px", fontWeight: 300, color: TERRACOTTA }}>
                      ${entry.totalDonated.toLocaleString("en-US", { minimumFractionDigits: 0 })}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--muted-foreground)", marginTop: "2px" }}>
                      donated
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Remaining entries */}
            {entries.slice(3).map((entry) => {
              const tier = entry.tier as string;
              const meta = TIER_META[tier] ?? TIER_META.none;
              const isCurrentUser = entry.isCurrentUser;
              const isBeyondTop20 = entry.rank > 20 && isCurrentUser;
              return (
                <React.Fragment key={entry.rank}>
                  {isBeyondTop20 && (
                    <div key={`divider-${entry.rank}`} style={{
                      display: "flex", alignItems: "center", gap: "10px",
                      margin: "8px 0", color: "var(--muted-foreground)", fontSize: "11px",
                    }}>
                      <div style={{ flex: 1, height: "1px", background: "var(--border)", borderStyle: "dashed" }} />
                      Your rank
                      <div style={{ flex: 1, height: "1px", background: "var(--border)", borderStyle: "dashed" }} />
                    </div>
                  )}
                  <div
                    key={entry.rank}
                    style={{
                      display: "flex", alignItems: "center", gap: "16px",
                      padding: "14px 22px",
                      border: isCurrentUser
                        ? "1px solid var(--primary)"
                        : "1px solid var(--border)",
                      borderRadius: "12px",
                    }}
                  >
                    <span style={{
                      minWidth: "32px", textAlign: "center",
                      fontSize: "13px", fontWeight: 300,
                      color: "var(--muted-foreground)",
                    }}>
                      #{entry.rank}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "15px", fontWeight: 300, color: "var(--foreground)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {entry.displayName}
                        </span>
                        {isCurrentUser && (
                          <span style={{
                            fontSize: "10px", padding: "1px 7px",
                            borderRadius: "10px", border: "1px solid var(--primary)",
                            color: "var(--primary)", fontWeight: 400,
                          }}>you</span>
                        )}
                        {tier !== "none" && (
                          <span style={{
                            fontSize: "11px", padding: "2px 9px",
                            borderRadius: "12px",
                            border: `1px solid ${meta.color}66`,
                            color: meta.color, background: `${meta.color}12`,
                          }}>
                            {meta.emoji} {meta.label}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "11px", color: "var(--muted-foreground)", marginTop: "2px" }}>
                        {entry.debtsErased} debt{entry.debtsErased !== 1 ? "s" : ""} erased
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: "15px", fontWeight: 300, color: TERRACOTTA }}>
                        ${entry.totalDonated.toLocaleString("en-US", { minimumFractionDigits: 0 })}
                      </div>
                      <div style={{ fontSize: "11px", color: "var(--muted-foreground)", marginTop: "2px" }}>
                        donated
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* CTA for non-signed-in */}
        {!isSignedIn && entries.length > 0 && (
          <div style={{
            marginTop: "48px", padding: "32px", textAlign: "center",
            border: "1px solid var(--border)", borderRadius: "16px",
          }}>
            <p style={{ color: "var(--muted-foreground)", fontWeight: 300, fontSize: "15px", marginBottom: "20px" }}>
              Ready to make an impact and earn your VIP badge?
            </p>
            <button
              onClick={() => navigate("/sign-up")}
              style={{
                background: TERRACOTTA, border: "none", borderRadius: "8px",
                padding: "11px 28px", color: "#fff", fontSize: "14px",
                fontWeight: 300, cursor: "pointer",
              }}
            >
              Start donating →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
